import { Context, defineCustomElement, getAttribute, getBooleanAttribute, getIntegerAttribute, getReactEventHandler, getRect, isAttrEqual, isAttrTrue, NectaryElement, setClass, updateAttribute, updateBooleanAttribute } from '../utils';
import { DEFAULT_SIZE } from '../utils/size';
const templateHTML = '<style>:host{display:block}#wrapper{display:flex;flex-direction:column;position:relative;width:100%;box-sizing:border-box;background-color:var(--sinch-comp-textarea-color-default-background-initial);border-radius:var(--sinch-local-shape-radius);padding-right:2px;overflow:hidden;--sinch-local-shape-radius:var(--sinch-comp-textarea-shape-radius)}#input{all:initial;display:block;font:var(--sinch-comp-textarea-font-input);color:var(--sinch-comp-textarea-color-default-text-initial);resize:none;white-space:pre-wrap;overflow-wrap:break-word;padding:8px 10px 8px 12px;border:none;box-sizing:border-box}#input::placeholder{color:var(--sinch-comp-textarea-color-default-text-placeholder);opacity:1}#input:disabled{color:var(--sinch-comp-textarea-color-disabled-text-initial);-webkit-text-fill-color:var(--sinch-comp-textarea-color-disabled-text-initial)}#border{position:absolute;border:1px solid var(--sinch-comp-textarea-color-default-border-initial);border-radius:var(--sinch-local-shape-radius);inset:0;pointer-events:none}:host([invalid]) #border{border-color:var(--sinch-comp-textarea-color-invalid-border-initial)}#input:focus+#border{border-color:var(--sinch-comp-textarea-color-default-border-focus);border-width:2px}#input:disabled+#border{border-color:var(--sinch-comp-textarea-color-disabled-border-initial)}#bottom{display:flex;flex-direction:row;align-items:center;gap:8px;padding:12px 4px 4px}#bottom.empty{display:none}#resize-handle{display:none;position:absolute;width:20px;height:20px;bottom:36px;right:0;cursor:ns-resize}:host([resizable]) #resize-handle{display:block}#bottom.empty+#resize-handle{bottom:0}#resize-icon{display:block;pointer-events:none;fill:var(--sinch-comp-textarea-color-default-border-initial)}</style><div id="wrapper"><textarea id="input"></textarea><div id="border"></div><div id="bottom"><slot name="bottom"></slot></div><div id="resize-handle"><svg id="resize-icon" width="16" height="16"><path d="m14.833 4.724-9.61 9.61-.942-.944 9.61-9.609.942.943ZM15.443 10 10.5 14.943 9.557 14 14.5 9.057l.943.943Z"/></svg></div></div>';
const template = document.createElement('template');
template.innerHTML = templateHTML;
defineCustomElement('sinch-textarea', class extends NectaryElement {
  #$input;
  #$bottomSlot;
  #$bottomWrapper;
  #$resizeHandle;
  #cursorPos = null;
  #isPendingDk = false;
  #controller = null;
  #sizeContext;
  #prevContentHeight = 0;
  #dragStartY = 0;
  #intersectionObserver = null;
  constructor() {
    super();
    const shadowRoot = this.attachShadow({
      delegatesFocus: true
    });
    shadowRoot.appendChild(template.content.cloneNode(true));
    this.#$input = shadowRoot.querySelector('#input');
    this.#$bottomSlot = shadowRoot.querySelector('slot[name="bottom"]');
    this.#$bottomWrapper = shadowRoot.querySelector('#bottom');
    this.#$resizeHandle = shadowRoot.querySelector('#resize-handle');
    this.#sizeContext = new Context(this.#$bottomWrapper, 'size');
  }
  connectedCallback() {
    super.connectedCallback();
    this.#controller = new AbortController();
    const options = {
      signal: this.#controller.signal
    };
    this.role = 'textbox';
    this.ariaMultiLine = 'true';
    this.#$input.addEventListener('input', this.#onInput, options);
    this.#$input.addEventListener('compositionstart', this.#onCompositionStart, options);
    this.#$input.addEventListener('mousedown', this.#onSelectionChange, options);
    this.#$input.addEventListener('keydown', this.#onSelectionChange, options);
    this.#$input.addEventListener('focus', this.#onInputFocus, options);
    this.#$input.addEventListener('blur', this.#onInputBlur, options);
    this.#$resizeHandle.addEventListener('mousedown', this.#onDragStart, options);
    this.#$bottomSlot.addEventListener('slotchange', this.#onBottomSlotChange, options);
    this.addEventListener('-change', this.#onChangeReactHandler, options);
    this.addEventListener('-focus', this.#onFocusReactHandler, options);
    this.addEventListener('-blur', this.#onBlurReactHandler, options);
    this.#sizeContext.listen(this.#controller.signal);
    this.#onBottomSlotChange();
    this.#updateMinRows();
    this.#onSizeUpdate();
  }
  disconnectedCallback() {
    super.disconnectedCallback();
    this.#controller.abort();
    this.#controller = null;
    if (this.#intersectionObserver !== null) {
      this.#intersectionObserver.disconnect();
      this.#intersectionObserver = null;
    }
  }
  static get observedAttributes() {
    return ['value', 'placeholder', 'invalid', 'disabled', 'rows', 'minrows', 'resizable'];
  }
  attributeChangedCallback(name, oldVal, newVal) {
    switch (name) {
      case 'value':
        {
          const nextVal = newVal ?? '';
          const prevVal = this.#$input.value;
          if (nextVal !== prevVal) {
            const prevCursorPos = this.#$input.selectionEnd;
            const isPrevCursorEnd = prevCursorPos === prevVal.length;
            const isShrinkingContent = nextVal.length < prevVal.length;
            this.#$input.value = nextVal;
            if (!this.resizable) {
              if (isShrinkingContent) {
                this.#$input.style.removeProperty('height');
              }
              const nextContentHeight = this.#$input.scrollHeight;
              if (isShrinkingContent || nextContentHeight !== this.#prevContentHeight) {
                this.#prevContentHeight = nextContentHeight;
                this.#$input.style.setProperty('height', `${this.#prevContentHeight}px`);
              }
            }
            if (!isPrevCursorEnd) {
              this.#$input.setSelectionRange(this.#cursorPos, this.#cursorPos);
            }
          }
          break;
        }
      case 'placeholder':
        {
          this.#$input.placeholder = newVal ?? '';
          updateAttribute(this, 'aria-placeholder', newVal);
          break;
        }
      case 'invalid':
        {
          if (isAttrEqual(oldVal, newVal)) {
            break;
          }
          const isInvalid = isAttrTrue(newVal);
          this.ariaInvalid = isInvalid.toString();
          updateBooleanAttribute(this, 'invalid', isInvalid);
          break;
        }
      case 'disabled':
        {
          if (isAttrEqual(oldVal, newVal)) {
            break;
          }
          const isDisabled = isAttrTrue(newVal);
          this.#$input.disabled = isDisabled;
          updateBooleanAttribute(this, 'disabled', isDisabled);
          break;
        }
      case 'rows':
        {
          updateAttribute(this.#$input, 'rows', newVal);
          break;
        }
      case 'minrows':
        {
          this.#updateMinRows();
          break;
        }
      case 'resizable':
        {
          if (isAttrEqual(oldVal, newVal)) {
            break;
          }
          updateBooleanAttribute(this, name, isAttrTrue(newVal));
          break;
        }
    }
  }
  set value(value) {
    updateAttribute(this, 'value', value);
  }
  get value() {
    return getAttribute(this, 'value', '');
  }
  set placeholder(value) {
    updateAttribute(this, 'placeholder', value);
  }
  get placeholder() {
    return getAttribute(this, 'placeholder');
  }
  set invalid(value) {
    updateBooleanAttribute(this, 'invalid', value);
  }
  get invalid() {
    return getBooleanAttribute(this, 'invalid');
  }
  set disabled(isDisabled) {
    updateBooleanAttribute(this, 'disabled', isDisabled);
  }
  get disabled() {
    return getBooleanAttribute(this, 'disabled');
  }
  set resizable(isResizable) {
    updateBooleanAttribute(this, 'resizable', isResizable);
  }
  get resizable() {
    return getBooleanAttribute(this, 'resizable');
  }
  set rows(value) {
    updateAttribute(this, 'rows', value);
  }
  get rows() {
    return getIntegerAttribute(this, 'rows', 0);
  }
  set minRows(value) {
    updateAttribute(this, 'minrows', value);
  }
  get minRows() {
    return getIntegerAttribute(this, 'minrows', 0);
  }
  get selectionStart() {
    return this.#$input.selectionStart;
  }
  set selectionStart(value) {
    this.#$input.selectionStart = value;
  }
  get selectionEnd() {
    return this.#$input.selectionEnd;
  }
  set selectionEnd(value) {
    this.#$input.selectionEnd = value;
  }
  get selectionDirection() {
    return this.#$input.selectionDirection;
  }
  set selectionDirection(value) {
    this.#$input.selectionDirection = value;
  }
  get focusable() {
    return true;
  }
  focus() {
    this.#$input.focus();
  }
  blur() {
    this.#$input.blur();
  }
  #updateMinRows() {
    const minRows = this.minRows;
    if (minRows <= 0) {
      this.#$input.style.removeProperty('min-height');
      if (this.#intersectionObserver !== null) {
        this.#intersectionObserver.disconnect();
        this.#intersectionObserver = null;
      }
    } else {
      if (this.isDomConnected) {
        this.#calcMinRows();
      }
      if (this.#intersectionObserver === null) {
        this.#intersectionObserver = new IntersectionObserver(this.#intersectionObserverCallback, {
          root: null
        });
        this.#intersectionObserver.observe(this.#$input);
      }
    }
  }
  #intersectionObserverCallback = _ref => {
    let [entry] = _ref;
    if (entry != null && entry.isIntersecting) {
      this.#calcMinRows();
    }
  };
  #calcMinRows() {
    this.#$input.rows = this.minRows;
    this.#$input.style.setProperty('min-height', `${getRect(this.#$input).height}px`);
    this.#$input.rows = this.rows;
  }
  #onCompositionStart = () => {
    this.#isPendingDk = true;
  };
  #onSelectionChange = () => {
    this.#cursorPos = this.#$input.selectionEnd;
  };
  #onInput = e => {
    e.stopPropagation();
    const nextValue = this.#$input.value;
    const prevValue = this.value;
    if (prevValue !== nextValue) {
      const nextCursorPos = this.#$input.selectionEnd;
      if (!this.#isPendingDk) {
        this.#$input.value = prevValue;
        const prevCursorPos = this.#cursorPos;
        const isPrevCursorEnd = prevCursorPos === null || prevCursorPos === prevValue.length;
        if (!isPrevCursorEnd) {
          this.#$input.setSelectionRange(prevCursorPos, prevCursorPos);
        }
      }
      this.#isPendingDk = false;
      this.#cursorPos = nextCursorPos;
      this.dispatchEvent(new CustomEvent('-change', {
        detail: nextValue
      }));
    }
  };
  #onDragStart = e => {
    if (this.#dragStartY !== 0) {
      return;
    }
    this.#dragStartY = e.clientY;
    this.#prevContentHeight = getRect(this.#$input).height;
    window.addEventListener('mousemove', this.#onDragResize, {
      signal: this.#controller.signal,
      capture: true
    });
    window.addEventListener('mouseup', this.#onDragEnd, {
      signal: this.#controller.signal,
      capture: true
    });
  };
  #onDragResize = e => {
    const dy = e.clientY - this.#dragStartY;
    const height = Math.round(Math.max(0, this.#prevContentHeight + dy));
    this.#$input.style.setProperty('height', `${height}px`);
  };
  #onDragEnd = () => {
    window.removeEventListener('mousemove', this.#onDragResize, {
      capture: true
    });
    window.removeEventListener('mouseup', this.#onDragEnd, {
      capture: true
    });
    this.#dragStartY = 0;
    this.#prevContentHeight = 0;
  };
  #onInputFocus = () => {
    this.dispatchEvent(new CustomEvent('-focus'));
  };
  #onInputBlur = () => {
    this.dispatchEvent(new CustomEvent('-blur'));
  };
  #onBottomSlotChange = () => {
    const isEmpty = this.#$bottomSlot.assignedElements().length === 0;
    setClass(this.#$bottomWrapper, 'empty', isEmpty);
  };
  #onSizeUpdate() {
    if (!this.isDomConnected) {
      return;
    }
    const size = this.getAttribute('data-size') ?? DEFAULT_SIZE;
    this.#sizeContext.dispatch(size);
  }
  #onChangeReactHandler = e => {
    getReactEventHandler(this, 'on-change')?.(e);
  };
  #onFocusReactHandler = () => {
    getReactEventHandler(this, 'on-focus')?.();
  };
  #onBlurReactHandler = () => {
    getReactEventHandler(this, 'on-blur')?.();
  };
});